import React, { useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { ConditionUtils } from "types/condition";
import * as ExpressionUtils from "utils/expression";
import * as RelationUtils from "utils/relation";
import DataIcon from "images/database-light.svg";
import JoinIcon from "images/Sense-Icons/JoinIcon.svg";
import AppendIcon from "images/Sense-Icons/AppendIcon.svg";
import DerivedIcon from "images/Sense-Icons/DerivedIcon.svg";
import FilterIcon from "images/Sense-Icons/FilterIcon.svg";
import Sliders from "images/Sense-Icons/Sliders.svg";
import BanIcon from "images/Sense-Icons/ban-light.svg";
import PivotIcon from "images/Sense-Icons/angle-90-light.svg";
import IfElseIcon from "images/Sense-Icons/IfElse.svg";
import DateIcon from "images/EDG/DateIcon.svg";
import CSVIconGray from "images/csv_light_grey.svg";
import Code from "images/Sense-Icons/Code.svg";
import styles from "./style.module.css";
import { Button, ButtonGroup } from "aible";
import RelationInput from "./RelationInput";
import { getDataset } from "containers/Home/reducer";
import { isBaseProjection, isNonScalarColumn, isScalar, projectionToExpression } from "types/expression";
import { filterUndefined } from "utils/collect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { findDataSourceDetails, getSchemaMonitor } from "containers/SchemaMonitor/reducer";
import { getDraft, rebaseDraft, resetDraft, resetDraftAndUnstageUpdates } from "containers/Relation/reducer";
import { useSelector } from "types/store";
import { RelationSavePanel } from "./RelationTile";
const LineageItem = ({ icon, label, subItem, date, details, style, fromDatasetLabel, onClick, onDelete, onReset, isExpanded, setIsExpanded }) => {
    return (React.createElement("div", { className: styles.lineageItem, style: style ? { ...style } : {} },
        React.createElement("div", { className: styles.lineageIcon },
            React.createElement("img", { src: icon })),
        React.createElement("div", { style: { flexGrow: 1 } },
            React.createElement("div", { style: { display: "flex", justifyContent: "space-between" } },
                React.createElement("div", { className: styles.normalBold }, label),
                React.createElement("div", null, (setIsExpanded || onReset || onDelete) && (React.createElement(ButtonGroup, null,
                    onReset && (React.createElement(Button, { onClick: () => onReset && onReset(), style: { padding: "5px", fontSize: "14px", lineHeight: "14px" } }, "Reset to Step")),
                    setIsExpanded && (React.createElement(Button, { active: isExpanded, onClick: () => setIsExpanded(!isExpanded), style: { padding: "5px", fontSize: "14px", lineHeight: "14px" } },
                        React.createElement(FontAwesomeIcon, { icon: faEdit }))),
                    onDelete && (React.createElement(Button, { onClick: () => onDelete && onDelete(), style: { padding: "5px", fontSize: "14px", lineHeight: "14px" } },
                        React.createElement(FontAwesomeIcon, { icon: faTrash }))))))),
            details ? React.createElement("div", { className: styles.sItem }, details) : "",
            React.createElement("div", { className: styles.sItem }, Array.isArray(subItem) ? subItem.map((x, i) => React.createElement("div", { key: i }, x)) : subItem),
            React.createElement("div", { className: styles.subItem }, date),
            fromDatasetLabel && (React.createElement("div", { className: styles.subItemDate, onClick: onClick },
                React.createElement("img", { src: DataIcon, style: { height: "12px" } }),
                onClick ? (React.createElement("a", { style: { marginLeft: "3px" } }, fromDatasetLabel)) : (React.createElement("span", { style: { marginLeft: "3px" } }, fromDatasetLabel)))))));
};
function formatDatasetUpdatedDate(dataset) {
    return dataset === undefined ? "" : moment.utc(dataset?.created_date).local().format("MMMM Do YYYY, h:mm:ss a");
}
const AggregateRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return (React.createElement(LineageItem, { icon: PivotIcon, label: "Aggregate Data", subItem: "", date: formatDatasetUpdatedDate(d), ...props }));
};
function getSourceIcon(source) {
    if (source.content.type == "RAW_DATA") {
        return CSVIconGray;
    }
    const details = findDataSourceDetails(source.content.provider);
    return details?.iconGray ?? details?.iconWhite ?? details?.image ?? DataIcon;
}
const DataSourceRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    const smid = relation.source.content.type !== "CONNECTION_METADATA" ? undefined : relation.source.content.schema_monitor_id;
    const sm = useSelector((state) => (smid === undefined ? undefined : getSchemaMonitor(state, smid)));
    if (relation.source.content.type === "CONNECTION_METADATA") {
        const details = findDataSourceDetails(relation.source.content.provider);
        const icon = details?.iconGray ?? details?.iconWhite ?? details?.image ?? DataIcon;
        const subItems = [];
        if (sm !== undefined) {
            subItems.push(`Connection: ${sm.name}`);
        }
        if (relation.source.content.table !== undefined) {
            subItems.push(`Table: ${relation.source.content.table}`);
        }
        if (subItems.length === 0 && d !== undefined) {
            subItems.push(`(${d.id}) ${d.name}`);
        }
        const label = details === undefined ? "Load From Data Source" : `Data Linked from ${details?.label}`;
        return React.createElement(LineageItem, { icon: icon, label: label, subItem: subItems, date: formatDatasetUpdatedDate(d), ...props });
    }
    return (React.createElement(LineageItem, { icon: getSourceIcon(relation.source), label: "Load From Data Source", subItem: d === undefined ? "" : `(${d.id}) ${d.name}`, date: formatDatasetUpdatedDate(d), ...props }));
};
const DistinctRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return (React.createElement(LineageItem, { icon: BanIcon, label: "Deduplicate Rows", subItem: relation.subset === undefined || relation.subset.length == 0 ? "" : `Distinct On: ${relation.subset.join(", ")}`, date: formatDatasetUpdatedDate(d), ...props }));
};
const ExcludeRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return (React.createElement(LineageItem, { icon: FilterIcon, label: "Remove Column" + (relation.exclude_columns.length > 1 ? "s" : ""), subItem: relation.exclude_columns, date: formatDatasetUpdatedDate(d), ...props }));
};
const FilterRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return (React.createElement(LineageItem, { icon: FilterIcon, label: "Filter Rows", subItem: `Filter: ${ConditionUtils.humanize(relation.filter)}`, date: formatDatasetUpdatedDate(d), ...props }));
};
const JoinRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.right.tag?.dataset_id));
    const joinLabel = relation.how === undefined || relation.how === "LEFT"
        ? "Left Join"
        : relation.how == "INNER"
            ? "Inner Join"
            : relation.how;
    const joinCondition = ExpressionUtils.andAll(relation.conditions.map((c) => ExpressionUtils.col(RelationUtils.getLeftKey(c)).eq(RelationUtils.getRightKey(c)))).build();
    const joinTarget = d === undefined ? " " : ` (${d.id}) ${d.name} `;
    return (React.createElement(LineageItem, { icon: JoinIcon, label: "Join Data", subItem: `${joinLabel}${joinTarget} on ${ConditionUtils.humanize(joinCondition)}`, date: formatDatasetUpdatedDate(d), ...props }));
};
const MutateRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    const types = relation.mutate
        .map((expr) => projectionToExpression(expr))
        .map((expr) => (!isScalar(expr) && !isNonScalarColumn(expr) ? expr.type : undefined))
        .filter(filterUndefined);
    const distinctTypes = new Set(types);
    const context = RelationUtils.getOutputContext(relation.input);
    const updated = [];
    const added = [];
    for (const expr of relation.mutate) {
        const alias = ExpressionUtils.getAlias(expr);
        if (context[alias] === undefined) {
            added.push(alias);
        }
        else {
            updated.push(alias);
        }
    }
    const subHeadings = [];
    if (added.length > 0) {
        subHeadings.push(`Added ${added.length} Column${added.length > 1 ? "s" : ""}`);
    }
    if (updated.length > 0) {
        subHeadings.push(`Updated ${updated.length} Column${updated.length > 1 ? "s" : ""}`);
    }
    if (distinctTypes.size == 1) {
        for (const t of distinctTypes.values()) {
            switch (t) {
                case "IF_THEN":
                    return (React.createElement(LineageItem, { icon: IfElseIcon, label: `Create If/Then/Else Rules`, subItem: subHeadings, date: formatDatasetUpdatedDate(d), ...props }));
                case "BINARY_OPERATION":
                case "FUNCTION":
                    return (React.createElement(LineageItem, { icon: DerivedIcon, label: `Create Derived Variables`, subItem: subHeadings, date: formatDatasetUpdatedDate(d), ...props }));
                case "DATE_PART":
                    return (React.createElement(LineageItem, { icon: DateIcon, label: `Create Derived Variables`, subItem: subHeadings, date: formatDatasetUpdatedDate(d), ...props }));
            }
        }
    }
    return (React.createElement(LineageItem, { icon: DerivedIcon, label: `Create Derived Variables`, subItem: subHeadings, date: formatDatasetUpdatedDate(d), ...props }));
};
const ProjectRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    const baseProjection = relation.project.filter(isBaseProjection);
    const renames = [];
    const casts = [];
    const context = RelationUtils.getOutputContext(relation.input);
    const outputContext = RelationUtils.getOutputContext(relation);
    const inputColumns = ExpressionUtils.getSchemaColumns(context);
    const outputColumnSet = new Set(ExpressionUtils.getSchemaColumns(outputContext));
    const excludeSet = new Set(inputColumns.filter((c) => !outputColumnSet.has(c)));
    for (const expr of baseProjection) {
        if (typeof expr === "string") {
            continue;
        }
        if ("type" in expr) {
            if (expr.alias != expr.expr.expr.column) {
                renames.push([expr.expr.expr.column, expr.alias]);
                excludeSet.delete(expr.expr.expr.column);
            }
            if (expr.expr.new_type != context[expr.expr.expr.column].type) {
                casts.push(expr);
            }
        }
        else if (expr.alias !== undefined && expr.column != expr.alias) {
            renames.push([expr.column, expr.alias]);
            excludeSet.delete(expr.column);
        }
    }
    const excludes = [...excludeSet];
    const subHeadings = [];
    if (excludes.length > 0) {
        subHeadings.push(React.createElement("div", { title: `Excluded Columns: ${excludes.join(", ")}` }, `Exclude ${excludes.length} Column${excludes.length > 1 ? "s" : ""}`));
    }
    if (renames.length > 0) {
        subHeadings.push(React.createElement("div", { title: `Renamed Columns: ${renames.map(([l, r]) => l + " -> " + r).join(", ")}` }, `Rename ${renames.length} Column${renames.length > 1 ? "s" : ""}`));
    }
    if (casts.length > 0) {
        subHeadings.push(`Update ${casts.length} Column Type${casts.length > 1 ? "s" : ""}`);
    }
    return (React.createElement(LineageItem, { icon: Sliders, label: `Update Columns`, subItem: subHeadings, date: formatDatasetUpdatedDate(d), ...props }));
};
const PivotRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return React.createElement(LineageItem, { icon: PivotIcon, label: "Pivot Data", subItem: "", date: formatDatasetUpdatedDate(d), ...props });
};
const UnpivotRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return (React.createElement(LineageItem, { icon: PivotIcon, label: "Unpivot Data", subItem: "", date: formatDatasetUpdatedDate(d), ...props }));
};
const UnpivotAndPivotRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return (React.createElement(LineageItem, { icon: PivotIcon, label: "Unpivot and Pivot Data", subItem: "", date: formatDatasetUpdatedDate(d), ...props }));
};
const SortRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return React.createElement(LineageItem, { icon: FilterIcon, label: "Sort Data", subItem: "", date: formatDatasetUpdatedDate(d), ...props });
};
const UnionRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    const unions = useSelector((state) => relation.inputs
        .map((r) => r.tag?.dataset_id)
        .filter(filterUndefined)
        .map((id) => {
        const x = getDataset(state, id);
        return x ? `(${x.id}) ${x.name}` : `(${id})`;
    }));
    return (React.createElement(LineageItem, { icon: AppendIcon, label: "Append Data", subItem: unions.slice(1).join(" and "), date: formatDatasetUpdatedDate(d), ...props }));
};
const SQLRelationSummaryView = ({ relation, ...props }) => {
    const d = useSelector((state) => getDataset(state, relation.tag?.dataset_id));
    return React.createElement(LineageItem, { icon: Code, label: "Custom SQL", subItem: "", date: formatDatasetUpdatedDate(d), ...props });
};
const RelationSummaryView = ({ relation, ...props }) => {
    switch (relation.type) {
        case "AGGREGATE":
            return React.createElement(AggregateRelationSummaryView, { relation: relation, ...props });
        case "DATA_SOURCE":
            return React.createElement(DataSourceRelationSummaryView, { relation: relation, ...props });
        case "DISTINCT":
            return React.createElement(DistinctRelationSummaryView, { relation: relation, ...props });
        case "EXCLUDE":
            return React.createElement(ExcludeRelationSummaryView, { relation: relation, ...props });
        case "FILTER":
            return React.createElement(FilterRelationSummaryView, { relation: relation, ...props });
        case "JOIN":
            return React.createElement(JoinRelationSummaryView, { relation: relation, ...props });
        case "MUTATE":
            return React.createElement(MutateRelationSummaryView, { relation: relation, ...props });
        case "PROJECT":
            return React.createElement(ProjectRelationSummaryView, { relation: relation, ...props });
        case "PIVOT":
            return React.createElement(PivotRelationSummaryView, { relation: relation, ...props });
        case "UNPIVOT":
            return React.createElement(UnpivotRelationSummaryView, { relation: relation, ...props });
        case "UNPIVOT_PIVOT":
            return React.createElement(UnpivotAndPivotRelationSummaryView, { relation: relation, ...props });
        case "SORT":
            return React.createElement(SortRelationSummaryView, { relation: relation, ...props });
        case "SQL":
            return React.createElement(SQLRelationSummaryView, { relation: relation, ...props });
        case "UNION":
            return React.createElement(UnionRelationSummaryView, { relation: relation, ...props });
    }
};
function empty(_) {
    _;
}
export const RelationLineageItem = ({ relation, onEdit, onDelete, onReset, readOnly }) => {
    const [isEditMode, updateIsEditMode] = useState(false);
    return (React.createElement("div", { style: { marginBottom: "20px" } },
        React.createElement(RelationSummaryView, { relation: relation, onDelete: onDelete, onReset: onReset, isExpanded: isEditMode, setIsExpanded: relation.type === "DATA_SOURCE" || readOnly ? undefined : updateIsEditMode }),
        isEditMode && relation.type !== "DATA_SOURCE" && (React.createElement("div", { style: { overflow: "auto", paddingLeft: "2px" } },
            React.createElement(RelationInput, { relation: relation, onChange: onEdit ?? empty, isDisabled: onEdit === undefined })))));
};
const RelationLineage = ({ datasetId, onSubmit, isMinimized, defaultOpenLineageSection }) => {
    const draft = useSelector((state) => getDraft(state, datasetId));
    const dispatch = useDispatch();
    const onChange = (relation) => dispatch(rebaseDraft(datasetId, relation));
    const relation = draft.base;
    const [openLineageSection, setOpenLineageSection] = useState(defaultOpenLineageSection);
    const showPendingDetail = openLineageSection === "pending";
    const showPriorDetail = openLineageSection === "prior";
    const inputList = RelationUtils.toList(relation);
    const context = RelationUtils.getOutputContext(relation);
    const errors = RelationUtils.validate(relation);
    const priorUpdates = [];
    const pendingUpdates = [];
    let hitPrior = false;
    for (const r of inputList) {
        if (r.tag?.dataset_id == draft.referenceDatasetId) {
            hitPrior = true;
            priorUpdates.push(r);
        }
        else if (r.type !== "DATA_SOURCE" && hitPrior) {
            pendingUpdates.push(r);
        }
        else {
            priorUpdates.push(r);
        }
    }
    const handleEditPending = (index, relation) => {
        const update = pendingUpdates
            .slice(index + 1)
            .reduce((update, r) => update.cherryPick(r), RelationUtils.builder(relation));
        onChange(update.build());
    };
    const handleDeletePending = (index) => {
        const update = pendingUpdates
            .filter((_, i) => i != index)
            .reduce((update, r) => update.cherryPick(r), RelationUtils.builder(priorUpdates[priorUpdates.length - 1]));
        onChange(update.build());
    };
    const priorUpdatesCount = priorUpdates.filter((r) => r.type !== "DATA_SOURCE").length;
    const priorUpdateLabel = priorUpdatesCount === 1 ? (isMinimized ? "UPDATE" : "UPDATE MADE") : isMinimized ? "UPDATES" : "UPDATES MADE";
    const pendingUpdateLabel = isMinimized ? "PENDING" : "PENDING UPDATES";
    const hasChanges = relation.tag?.dataset_id !== datasetId;
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.updateTiles },
            React.createElement("div", { className: styles.uFlex },
                React.createElement("div", { className: styles.updateTileFlex },
                    React.createElement("div", { className: styles.tTop },
                        React.createElement("span", null, priorUpdatesCount)),
                    React.createElement("div", { className: styles.tBot }, !isMinimized && priorUpdatesCount > 0 ? (React.createElement("a", { style: openLineageSection === "prior" ? { textDecoration: "underline" } : {}, onClick: () => setOpenLineageSection(openLineageSection === "prior" ? undefined : "prior") }, priorUpdateLabel)) : (priorUpdateLabel))),
                React.createElement("div", { className: styles.updateTileFlex },
                    React.createElement("div", { className: styles.tTop },
                        React.createElement("span", null, pendingUpdates.length)),
                    React.createElement("div", { className: styles.tBot }, !isMinimized && pendingUpdates.length > 0 ? (React.createElement("a", { style: openLineageSection === "pending" ? { textDecoration: "underline" } : {}, onClick: () => setOpenLineageSection(openLineageSection === "pending" ? undefined : "pending") }, pendingUpdateLabel)) : (pendingUpdateLabel))))),
        !isMinimized && (React.createElement("div", { className: styles.lineageItems, style: priorUpdates && priorUpdates.length > 0 ? { paddingBottom: "0px" } : {} },
            showPendingDetail && hasChanges && (React.createElement("div", { className: styles.pendingUpdates, id: "pendingUpdates_" + datasetId },
                pendingUpdates
                    .slice()
                    .reverse()
                    .map((r, i) => (React.createElement(RelationLineageItem, { key: i, relation: r, onEdit: (update) => handleEditPending(pendingUpdates.length - i - 1, update), onDelete: () => handleDeletePending(pendingUpdates.length - i - 1) }))),
                React.createElement(Button, { style: { padding: "4px", marginRight: "5px", fontSize: "12px", lineHeight: "12px" }, onClick: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        dispatch(resetDraft(datasetId));
                    } }, "Reset All Pending Updates"))),
            showPriorDetail && priorUpdates && (React.createElement("div", { id: "lastUpdates_" + datasetId }, priorUpdates
                .slice()
                .reverse()
                .map((r, i) => (React.createElement(RelationLineageItem, { key: i, relation: r, onReset: r.tag?.dataset_id === undefined || i == 0
                    ? undefined
                    : () => {
                        dispatch(resetDraftAndUnstageUpdates(datasetId, r.tag?.dataset_id));
                        setOpenLineageSection("pending");
                    } }))))))),
        !isMinimized && onSubmit && hasChanges && (React.createElement(RelationSavePanel, { datasetId: datasetId, onSubmit: onSubmit, context: context, draft: draft, baseList: inputList, errors: errors, hasChanges: hasChanges }))));
};
export default RelationLineage;
